/**
 * @fileoverview Mock backend for the shared Tenant resource.
 * @description Registers GET /sales/tenants (list, with filters), GET /sales/tenants/:id (the
 * "View Tenant" detail payload — profile, current target summary, and activity count), POST
 * /sales/tenants (create prospect), POST /sales/tenants/:id/approve, and POST
 * /sales/tenants/:id/archive|restore on the feature's shared mock adapter (see
 * sales-mock-adapter.ts), backed by tenants-mock-store.ts. Creating applies the maker-checker rules
 * (see prospect-approval.utils.ts): a Super Admin's or Sales Head's prospect is live at once, owned
 * by its assignee if one was chosen; a Sales Manager's or Sales Executive's waits for their Sales
 * Head, keeping the chosen assignee (always the maker, for a Sales Executive) until approval makes
 * them its owner. An assignee only becomes the owner — the target is applied separately (Assign
 * Target), so an owned prospect without a target stays in Sales Pipeline's Backlog. A real backend also emails the approver a link to the prospect
 * (Sales Pipeline's `?tab=pending&prospect=<id>`); the mock has no email to send. See
 * docs/sales-tenant-api-contract.md for the endpoint contract. A no-op when VITE_ENABLE_MOCK_API is
 * false.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import {
  canApproveProspect,
  prospectApproverFor,
  prospectNeedsApproval,
} from '@/features/sales/lib/prospect-approval.utils'
import { listActivitiesForTenant } from '@/features/sales/mocks/activities-mock-store'
import { listSalesRoster } from '@/features/sales/mocks/sales-roster-mock-store'

import { salesMockAdapter } from './sales-mock-adapter'
import {
  approveTenant,
  archiveTenant,
  createTenant,
  findTenantById,
  listTenants,
  recordTenantOwnershipChange,
  restoreTenant,
  updateTenant,
} from './tenants-mock-store'

import type {
  CreateTenantInput,
  ProspectApprovalDetails,
  TenantFilters,
  UpdateTenantInput,
} from './tenants-mock-store'
import type { Tenant } from '@/features/sales/types/tenant.types'

/** POST /sales/tenants' body: the prospect, its optional assignee, and who is creating it. */
export interface CreateTenantBody extends CreateTenantInput {
  ownerId?: string | null
  actingUserId: string
  actingUserRole: string
}

/**
 * @description Makes a user a prospect's owner — without a target, which is applied separately.
 * @param {Tenant} tenant - The prospect.
 * @param {string} ownerId - Its new owner.
 * @param {string} actingUserId - Who is assigning it.
 */
function assignOwner(tenant: Tenant, ownerId: string, actingUserId: string): void {
  recordTenantOwnershipChange(tenant.id, {
    fromOwnerId: tenant.currentOwnerId,
    toOwnerId: ownerId,
    actingUserId,
    transferredAt: new Date().toISOString(),
  })
}

/**
 * @description Works out a new prospect's approval details from its maker (see
 * prospect-approval.utils.ts).
 * @param {string} actingUserId - The maker's roster id.
 * @param {string} actingUserRole - The maker's active role name.
 * @param {string | null} [ownerId] - The assignee the maker chose, if any.
 * @returns {ProspectApprovalDetails} The approval details to store.
 */
function approvalDetailsFor(
  actingUserId: string,
  actingUserRole: string,
  ownerId?: string | null,
): ProspectApprovalDetails {
  if (!prospectNeedsApproval(actingUserRole)) {
    return { approval: 'Approved', createdById: actingUserId || null }
  }
  return {
    approval: 'Pending Approval',
    createdById: actingUserId,
    approverId: prospectApproverFor(actingUserId, listSalesRoster()),
    requestedOwnerId: actingUserRole === 'Sales Executive' ? actingUserId : (ownerId ?? null),
  }
}

const NOT_YOUR_APPROVAL =
  "Only this prospect's Sales Head, or Super Admin, can approve it while it's pending."

/**
 * @description Creates a prospect under the maker-checker rules: live at once (owned by its
 * assignee, if one was given) for a Super Admin or Sales Head, pending its Sales Head's approval
 * for a Sales Manager or Executive.
 * @param {CreateTenantBody} body - The create request.
 * @returns {Tenant} The created prospect, as stored after any assignment.
 */
export function createProspect(body: CreateTenantBody): Tenant {
  const { ownerId, actingUserId, actingUserRole, ...input } = body
  const approval = approvalDetailsFor(actingUserId, actingUserRole, ownerId)
  const created = createTenant(input, approval)

  if (approval.approval === 'Approved' && ownerId) {
    assignOwner(created, ownerId, actingUserId)
  }
  return findTenantById(created.id) ?? created
}

/**
 * @description Approves a pending prospect, then makes the assignee its maker chose (if any) its
 * owner — still without a target, which is applied separately.
 * @param {string} id - The pending prospect.
 * @param {string} actingUserId - Who is approving it.
 * @param {string} actingUserRole - Their active role name.
 * @returns {Tenant | { error: string } | null} The approved prospect; an error when the acting user
 * may not approve it (or it isn't pending); or null if no prospect has that id.
 */
export function approveProspect(
  id: string,
  actingUserId: string,
  actingUserRole: string,
): Tenant | { error: string } | null {
  const tenant = findTenantById(id)
  if (!tenant) return null
  if (!canApproveProspect(tenant, actingUserId, actingUserRole)) return { error: NOT_YOUR_APPROVAL }

  const approved = approveTenant(id)
  if (approved?.requestedOwnerId) {
    assignOwner(approved, approved.requestedOwnerId, actingUserId)
  }
  return findTenantById(id) ?? null
}

/** The "View Tenant" detail payload — a Tenant plus its activity count, per every spec's "View
 * Tenant ... includes ... activity history". The activities themselves are fetched separately via
 * GET /sales/tenants/:id/activities (see activities-mock.service.ts) rather than embedded here, so
 * a large history doesn't bloat every detail fetch. */
export interface TenantDetail extends Tenant {
  activityCount: number
}

if (salesMockAdapter) {
  salesMockAdapter.onGet('/sales/tenants').reply((config) => {
    const { ownerIds, ...rest } = (config.params ?? {}) as Partial<TenantFilters> & {
      ownerIds?: string
    }
    const filters: TenantFilters = {
      ...rest,
      ...(ownerIds ? { ownerIds: ownerIds.split(',') } : {}),
    }
    const response: Tenant[] = listTenants(filters)
    return [200, response]
  })

  salesMockAdapter.onGet(/^\/sales\/tenants\/[^/]+$/).reply((config) => {
    const id = config.url?.split('/').at(-1)
    const tenant = id ? findTenantById(id) : undefined

    if (!tenant) {
      return [404, { appCode: '1404', message: 'Tenant not found.' }]
    }

    const response: TenantDetail = {
      ...tenant,
      activityCount: listActivitiesForTenant(tenant.id).length,
    }
    return [200, response]
  })

  salesMockAdapter
    .onPost('/sales/tenants')
    .reply((config) => [201, createProspect(JSON.parse(config.data as string) as CreateTenantBody)])

  salesMockAdapter.onPost(/^\/sales\/tenants\/[^/]+\/approve$/).reply((config) => {
    const id = config.url?.split('/').at(-2) ?? ''
    const { actingUserId, actingUserRole } = JSON.parse(config.data as string) as {
      actingUserId: string
      actingUserRole: string
    }
    const result = approveProspect(id, actingUserId, actingUserRole)

    if (!result) {
      return [404, { appCode: '1404', message: 'Tenant not found.' }]
    }
    if ('error' in result) {
      return [422, { appCode: '1422', message: result.error }]
    }
    return [200, result]
  })

  salesMockAdapter.onPatch(/^\/sales\/tenants\/[^/]+$/).reply((config) => {
    const id = config.url?.split('/').at(-1)
    const { actingUserId, actingUserRole, ...input } = JSON.parse(
      config.data as string,
    ) as UpdateTenantInput & {
      actingUserId: string
      actingUserRole: string
    }
    const result = id ? updateTenant(id, input, actingUserId, actingUserRole) : null

    if (!result) {
      return [404, { appCode: '1404', message: 'Tenant not found.' }]
    }
    if ('error' in result) {
      // 422, not 403 — see targets-mock.service.ts's stage handler for why.
      return [422, { appCode: '1422', message: result.error }]
    }
    return [200, result]
  })

  salesMockAdapter.onPost(/^\/sales\/tenants\/[^/]+\/archive$/).reply((config) => {
    const id = config.url?.split('/').at(-2)
    const { reason } = JSON.parse(config.data as string) as { reason: string }

    if (!reason?.trim()) {
      return [422, { appCode: '1422', message: 'A reason note is required to archive a prospect.' }]
    }

    const updated = id ? archiveTenant(id, reason) : null
    if (!updated) {
      return [404, { appCode: '1404', message: 'Tenant not found.' }]
    }
    return [200, updated]
  })

  salesMockAdapter.onPost(/^\/sales\/tenants\/[^/]+\/restore$/).reply((config) => {
    const id = config.url?.split('/').at(-2)
    const { reason } = JSON.parse(config.data as string) as { reason: string }

    if (!reason?.trim()) {
      return [422, { appCode: '1422', message: 'A reason note is required to restore a prospect.' }]
    }

    const updated = id ? restoreTenant(id) : null
    if (!updated) {
      return [404, { appCode: '1404', message: 'Tenant not found.' }]
    }
    return [200, updated]
  })
}
